import { RiSearchLine } from "@remixicon/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

interface BrandRow {
	brandName: string;
	isActive: boolean;
	articleCount: number;
}

const QUERY_KEY = ["prohandel", "brands"] as const;

/// Brand switches - the brand dimension of the Prohandel sync filter,
/// operated like branch toggles with season semantics: switching a brand
/// OFF purges its articles from the DB (per-row discounts/edits die with
/// them - the confirm dialog says so); switching it back ON triggers a
/// fast_in_stock re-import (~40 min, Prohandel has no per-brand endpoint).
/// Deactivated brands stay listed (registry) so they can be re-activated.
export function BrandTable() {
	const qc = useQueryClient();
	const [search, setSearch] = useState("");
	const [confirmBrand, setConfirmBrand] = useState<BrandRow | null>(null);

	const query = useQuery({
		queryKey: QUERY_KEY,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/api/v1/prohandel/brands" as never);
			if (error) throw new Error("failed to load brands");
			return data as BrandRow[];
		},
	});

	const mutation = useMutation({
		mutationFn: async (input: { brandName: string; isActive: boolean }) => {
			const { error } = await fetchClient.PATCH(
				"/api/v1/prohandel/brands" as never,
				{ body: input } as never,
			);
			if (error) {
				// AppError wire shape is { error, detail } - the human-readable
				// explanation (e.g. "a sync is currently running") is `detail`.
				const e = error as { detail?: string; error?: string; message?: string };
				throw new Error(e.detail ?? e.message ?? e.error ?? "Toggling the brand failed");
			}
			return input;
		},
		onSuccess: (input) => {
			qc.invalidateQueries({ queryKey: QUERY_KEY });
			if (input.isActive) {
				toast.success(
					`${input.brandName} re-activated - re-import started (~40 min). Progress shows in the sync bar.`,
				);
			} else {
				toast.success(`${input.brandName} deactivated - articles removed from the catalogue.`);
			}
		},
		onError: (e: Error) => toast.error(e.message),
	});

	function handleToggle(brand: BrandRow, next: boolean) {
		if (!next) {
			// Deactivation is destructive (purge) - confirm first.
			setConfirmBrand(brand);
			return;
		}
		mutation.mutate({ brandName: brand.brandName, isActive: true });
	}

	if (query.isLoading) {
		return (
			<div className="space-y-2">
				{[0, 1, 2].map((i) => (
					<Skeleton key={i} className="h-9 w-full" />
				))}
			</div>
		);
	}

	const brands = query.data ?? [];
	if (brands.length === 0) {
		return (
			<p className="text-sm text-muted-foreground">
				No brands yet - run a Prohandel sync first. Brands appear here automatically.
			</p>
		);
	}

	const needle = search.trim().toLowerCase();
	const visible = needle
		? brands.filter((b) => b.brandName.toLowerCase().includes(needle))
		: brands;
	const inactiveCount = brands.filter((b) => !b.isActive).length;

	return (
		<div className="space-y-3">
			<div className="flex items-center gap-3">
				<div className="relative flex-1 max-w-sm">
					<RiSearchLine className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
					<Input
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Search brands..."
						className="pl-8"
					/>
				</div>
				<span className="text-xs text-muted-foreground whitespace-nowrap">
					{brands.length} brands
					{inactiveCount > 0 && ` · ${inactiveCount} deactivated`}
				</span>
			</div>

			<div className="max-h-[420px] overflow-y-auto rounded-md border">
				<Table>
					<TableHeader className="sticky top-0 bg-background">
						<TableRow>
							<TableHead>Brand</TableHead>
							<TableHead className="text-right w-28">Articles</TableHead>
							<TableHead className="text-right w-24">Active</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{visible.map((b) => (
							<TableRow key={b.brandName} className={b.isActive ? "" : "opacity-60"}>
								<TableCell className="font-medium">
									{b.brandName}
									{!b.isActive && (
										<Badge variant="secondary" className="ml-2 text-[10px]">
											off
										</Badge>
									)}
								</TableCell>
								<TableCell className="text-right tabular-nums">{b.articleCount}</TableCell>
								<TableCell className="text-right">
									<Switch
										checked={b.isActive}
										disabled={mutation.isPending}
										onCheckedChange={(v) => handleToggle(b, !!v)}
									/>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>

			<AlertDialog open={confirmBrand !== null} onOpenChange={(o) => !o && setConfirmBrand(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Deactivate {confirmBrand?.brandName}?</AlertDialogTitle>
						<AlertDialogDescription>
							This removes all {confirmBrand?.articleCount} articles of {confirmBrand?.brandName}{" "}
							from the catalogue - including their commercial discounts, cell edits and export
							flags. Re-activating the brand later re-imports the articles from Prohandel (~40 min)
							but does NOT bring those edits back.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (confirmBrand) {
									mutation.mutate({ brandName: confirmBrand.brandName, isActive: false });
								}
								setConfirmBrand(null);
							}}
						>
							Deactivate
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
